<template>
  <div
    class="slider-container"
    @touchend="handleEnd"
    @touchmove="handleMove"
    @touchstart="handleStart"
    @mousedown.prevent="handleStart"
    @mouseleave.prevent="handleEnd"
    @mousemove.prevent="handleMove"
    @mouseup.prevent="handleEnd"
  >
    <div
      v-for="(slide, index) in slides"
      v-show="index === currentSlide"
      :key="index"
    >
      <slot :slide="slide"></slot>
    </div>
    <div
      v-if="slides.length > 1"
      class="navigation-dots"
    >
      <span
        v-for="(_, index) in slides"
        :key="index"
        :class="{ active: currentSlide === index }"
        class="dot"
        @click="() => goToSlide(index)"
      ></span>
    </div>
  </div>
</template>

<script lang="ts" setup generic="T">
import { ref, VNode } from 'vue'

const { slides = [] } = defineProps<{
  slides: T[]
  roundCorners?: boolean
}>()

defineSlots<{
  default?(props: { slide: T }): VNode
}>()

const isDragging = ref(false)
const currentSlide = ref(0)
const threshold = 50
const startX = ref(0)
const startY = ref(0)
const diffX = ref(0)

const getEventX = (event: TouchEvent | MouseEvent) => ('touches' in event ? event.touches[0].clientX : event.clientX)
const getEventY = (event: TouchEvent | MouseEvent) => ('touches' in event ? event.touches[0].clientY : event.clientY)

const goToSlide = (index: number) => {
  currentSlide.value = index
}

const handleStart = (event: TouchEvent | MouseEvent) => {
  isDragging.value = true
  startX.value = getEventX(event)
  startY.value = getEventY(event)
}

const handleMove = (event: TouchEvent | MouseEvent) => {
  if (!isDragging.value) return
  const moveX = getEventX(event)
  const moveY = getEventY(event)

  diffX.value = Math.abs(startX.value - moveX)
  const diffY = Math.abs(startY.value - moveY)

  if (diffY < diffX.value) {
    event.preventDefault()
  }
}

const handleEnd = () => {
  if (!isDragging.value) return

  if (diffX.value > threshold) {
    if (diffX.value > 0) {
      goToSlide((currentSlide.value + 1) % slides.length)
    } else {
      goToSlide((currentSlide.value - 1 + slides.length) % slides.length)
    }
  }
  isDragging.value = false
}
</script>

<style lang="scss" scoped>
.slider-container {
  position: relative;
  overflow: hidden;
  user-select: none;
}

.navigation-dots {
  position: absolute;
  bottom: 1rem;
  width: 100%;
  display: flex;
  justify-content: center;
}

.dot {
  height: 0.8rem;
  width: 0.8rem;
  margin: 0 0.5rem;
  background-color: var(--blue-lightest);
  border-radius: 50%;
  display: inline-block;
  cursor: pointer;
}

.dot.active {
  background-color: var(--blue-light);
}
</style>
